import { useCallback } from 'react';
import { Stack, Group, Center, Button, Badge, CSSObject } from '@mantine/core';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';
import { textVariants } from '@ui/tokens/typography/typography';
import useCurrentUserProjectPermissions from '@projectMembers/hooks/useCurrentUserProjectPermissions';
import useRequestUpgradeRoleMutation from '@projects/queries/useRequestUpgradeRoleMutation';
import { trackManagerPermissionsUpgradeRequested } from 'src/services/tracker/trackers/projects';
import GuestHash from '@projects/models/guestHash';

import Project from '../../../../../entities/project';

import styles from './ShareInviteLink.module.scss';

import InviteLinks from './InviteLinks/InviteLinks';

export type ShareInviteLinkProps = {
	project : Project;
	onInviteLinkChange?: ( guestHash: GuestHash ) => void;
};

const BADGE_TEXT_STYLES = {
	inner: {
		color: 'var(--mantine-color-black)',
		...( textVariants[ 'caption-semibold' ] as CSSObject )
	}
};

const ShareInviteLink = ( { project, onInviteLinkChange }: ShareInviteLinkProps ) => {
	const permissions = useCurrentUserProjectPermissions( project.id );
	const currentUserCanShareInviteLink = permissions.currentUserCan( 'shareInviteLink', 'projects' );
	const { mutate: requestUpgradeRole } = useRequestUpgradeRoleMutation();

	const onRequestPermissionClick = useCallback( () => {
		trackManagerPermissionsUpgradeRequested( { projectID: project.id } );
		requestUpgradeRole( { projectID: project.id } );
	}, [ requestUpgradeRole, project.id ] );

	return (
		<Stack className={styles.ShareInviteLink} spacing={12}>
			<Stack spacing={4}>
				<Group align="center" spacing={6}>
					<Center bg="blue.1" p={4} style={{ borderRadius: 4 }}>
						<Icon icon="public" size={14} color="blue.5" />
					</Center>
					<Text variant="footnote-semibold" color="dark.7">Share link invite</Text>
					<Badge color="blue" size="xs" styles={BADGE_TEXT_STYLES}>No Account Required</Badge>
				</Group>
				<Text variant="footnote" color="dark.3">
					Anyone can access if the link is open and assume the invite role.
				</Text>
			</Stack>

			{currentUserCanShareInviteLink ? (
				<InviteLinks
					project={project}
					onInviteLinkChange={onInviteLinkChange}
				/>
			) : (
				<Group spacing={8} position="apart" bg="blue.0" p={12} style={{ borderRadius: 4 }}>
					<Text variant="footnote" color="black">
						Only managers can enable and send share links
					</Text>
					<Button h={19} variant="subtle" color="primary.5" size="xs" w="fit-content" compact p={0} onClick={onRequestPermissionClick}>
						Request permission
					</Button>
				</Group>
			)}
		</Stack>
	);
};

export default ShareInviteLink;
